import React, { useState } from "react";

// COMMOM Import
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import ICONS from "../../../../assets/Icons";
import SVGComponent from "../../SVGComponent";

// UTLIS Import
import { linkIdentifier } from "../../../../utils/linkIdentifier";
import {
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";

// Suggested prop reference for consumers -------------------------------------
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void)} OnClickEventHandler
 */
/**
 * @typedef {("button" | "submit")} BtnType
 */
/**
 * @typedef {("white" | "black")} HoverOption
 */
/**
 * @typedef {Object} ChipItem
 * @property {string} text - Chip label.
 * @property {boolean} selected - Highlight chip and render bullet list when true.
 * @property {boolean} [disabled] - Disable click/hover behaviour.
 * @property {HoverOption} [hoverOption] - Hover theme for enabled chips.
 * @property {string[]} [bulletPoint] - Optional bullet items shown inline when selected or when disabled.
 * @property {string} [imgUrl] - Optional row image/icon shown before label.
 * @property {string} [image] - Alias for imgUrl.
 * @property {string} [icon] - Alias for imgUrl (used by switch-account chips).
 * @property {string} [imgColor] - Optional SVG tint color when imgUrl is used.
 * @property {(e: React.MouseEvent<HTMLDivElement>, index: number, chip: ChipItem) => void} [onClick] - Chip click handler.
 */
const EditCommonPopup = ({
  headerTitle,
  headerWithIcon,
  subtext,
  btnOneBtnType,
  btnOneDisabledTextColor,
  btnOneDisabledBgColor,
  btnOneIsLoading,
  btnOneButtonRef,
  btnOneInActiveVariantNeeded,
  btnOneIsLoaderNeeded,
  btnOneHandleFunction,
  btnOneEnabledBgColor,
  btnOneOutLineColor,
  btnOneEnabledTextColor,
  btnOneIsDisabled,
  btnOneHoverOption,
  btnOneFullWidthRequired,
  btnTwoText,
  btnTwoBtnType,
  btnTwoDisabledTextColor,
  btnTwoIsLoading,
  btnTwoButtonRef,
  btnTwoInActiveVariantNeeded,
  btnTwoIsLoaderNeeded,
  btnTwoHandleFunction,
  btnTwoEnabledBgColor,
  btnTwoDisabledBgColor,
  btnTwoOutLineColor,
  btnTwoEnabledTextColor,
  btnTwoIsDisabled,
  btnTwoFullWidthRequired,
  btnTwoHoverOption,
  isalert,
  alertText,
  alertImage,
  alertBackground,
  chipsArray,
  defaultHeight,
  activeAccountType,
}) => {
  // useState
  const [animate, setAnimate] = useState(false);
  const [rowSelected, setrowSelected] = useState(false);

  const safeBtnOneHandler =
    typeof btnOneHandleFunction === "function"
      ? btnOneHandleFunction
      : () => {};
  const safeBtnTwoHandler =
    typeof btnTwoHandleFunction === "function"
      ? btnTwoHandleFunction
      : () => {};
  const safeChipsArray = Array.isArray(chipsArray) ? chipsArray : [];

  const hasBulletPoints = (item) =>
    item.bulletPoint &&
    Array.isArray(item.bulletPoint) &&
    item.bulletPoint.length > GlobalConfiguration.lengthCheckZero;

  const getRowImageUrl = (item) =>
    item?.imgUrl || item?.image || item?.icon || "";

  const hasRowImage = (item) => Boolean(getRowImageUrl(item));

  const isDisabledWithBullets = (item) =>
    item.disabled && hasBulletPoints(item);

  const selectedItemHasBullets = safeChipsArray.some(
    (chip) => chip.selected && !chip.disabled && hasBulletPoints(chip),
  );

  const getRowHeightClass = (item) => {
    if (item.selected && !item.disabled && hasBulletPoints(item)) {
      const isSingleBullet =
        item.bulletPoint?.length === GlobalConfiguration.lengthCheckOne;

      // 1 bullet: fit content (no extra gap). 2+ bullets: keep fixed height + animation.
      if (isSingleBullet) {
        return "h-auto";
      }

      return activeAccountType
        ? "h-auto Qsmd:h-accountType-popup-row-selected"
        : "h-auto Qsmd:h-edit-popup-row-selected";
    }
    if (
      rowSelected &&
      !item.disabled &&
      !item.selected &&
      defaultHeight !== null &&
      selectedItemHasBullets
    ) {
      return activeAccountType
        ? "h-accountType-popup-row-after-selection"
        : "h-edit-popup-row-after-selection";
    }
    if (isDisabledWithBullets(item)) {
      return "Qsmd:h-edit-popup-row-ifsc h-auto";
    }
    return "h-edit-popup-row-default";
  };

  const toSentenceCase = (text = "") =>
    text.charAt(GlobalConfiguration?.lengthCheckZero).toUpperCase() +
    text.slice(GlobalConfiguration?.lengthCheckOne).toLowerCase();

  const needsFullStop = (text = "") => !text.trim().endsWith(".");

  const shouldShowBullets = (item) => {
    if (!hasBulletPoints(item)) {
      return false;
    }
    if (item.selected && !item.disabled) return true;
    if (item.disabled) return true;
    return false;
  };

  return (
    <div
      className="flex w-full h-screen justify-center items-center m-auto top-top-half left-left-half fixed z-z-index-modal p-padding-sm"
      style={{ transform: "translate(-50%, -50%)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className={`max-h-alert-popup-height-1 xxl:max-h-alert-popup-height-2 tall:max-h-alert-popup-height-2 overflow-hidden w-full Qmd:w-small-popup-width flex flex-col py-padding-md bg-primary-bg rounded-border-radius-md animate-none ${animate ? "Qmd:animate-slideInFromLeft" : "Qmd:animate-slideInFromRight"}`}
      >
        <div className="h-full flex flex-col flex-1 overflow-hidden min-h-0">
          <div
            className={`flex flex-col flex-shrink-0 px-padding-md ${subtext ? "gap-gap-xs" : ""}`}
          >
            <div className="flex gap-gap-sm">
              <img
                src={headerWithIcon}
                alt=""
                className={`h-icon-md w-icon-md ${!headerWithIcon && "hidden"} `}
              />

              <p className={`text-text-primary   text-font-md  font-semibold `}>
                {toSentenceCase(headerTitle)}
              </p>
            </div>

            {subtext && (
              <span className="text-font-xs text-text-secondary font-normal ">
                {linkIdentifier(subtext)}
                {needsFullStop(subtext) && <span>.</span>}
              </span>
            )}
          </div>

          {isalert && (
            <div className="pt-padding-md px-padding-md flex-shrink-0">
              <div
                className={`bg-${alertBackground} flex items-center  rounded-border-radius-md gap-gap-sm p-padding-sm`}
              >
                <img
                  className="h-icon-md w-icon-md"
                  src={alertImage}
                  alt=""
                />
                {alertText && (
                  <p className={` text-text-primary text-font-xs`}>
                    {linkIdentifier(alertText)}
                    {needsFullStop(alertText) && <span>.</span>}
                  </p>
                )}
              </div>
            </div>
          )}

          <div
            className={` mt-margin-md flex-1 min-h-0 overflow-y-auto px-padding-md `}
          >
            <div className="border border-primary-act bg-primary-card rounded-border-radius-md overflow-hidden">
              {safeChipsArray.map((item, index) => {
                const isExpanded = item.selected && !item.disabled;
                const isDisabledExpanded = isDisabledWithBullets(item);
                const shouldUseBulletLayout =
                  hasBulletPoints(item) && (isExpanded || isDisabledExpanded);
                // transition-[height] duration-animation-fast ease-in-out ${getRowHeightClass(item)}
                return (
                  <div
                    key={index}
                    className={`box-border overflow-hidden flex gap-gap-sm px-padding-md  py-padding-md transition-[height] duration-animation-medium  ease-[cubic-bezier(0.68,-0.55,0.27,1.55)]  ${getRowHeightClass(item)}
                      ${
                        shouldUseBulletLayout
                          ? "items-start pt-padding-md "
                          : "items-center"
                      } ${
                        index !== safeChipsArray.length - 1
                          ? "border-b border-primary-act"
                          : ""
                      } ${
                        isExpanded
                          ? "bg-primary-nav-act cursor-pointer"
                          : item.disabled
                            ? ""
                            : item.hoverOption
                              ? `global-hover-${item.hoverOption} z-z-index-content relative cursor-pointer `
                              : "cursor-pointer"
                      } ${item.disabled ? "cursor-not-allowed  global-bg-black-color  " : ""}`}
                    onClick={(e) => {
                      if (!item.disabled && item?.onClick) {
                        item.onClick(e, index, item);
                        setrowSelected(true);

                        // If the user clicks the last item in the list, automatically
                        // scroll it up smoothly so it stays visible while expanding.
                        if (index === safeChipsArray.length - 1) {
                          const targetElement = e.currentTarget;
                          setTimeout(() => {
                            targetElement.scrollIntoView({
                              behavior: "smooth",
                              block: "end",
                            });
                          }, 50); // Small timeout ensures the CSS height class starts changing first
                        }
                        // ---------------------------
                      }
                    }}
                  >
                    {hasRowImage(item) && (
                      <div
                        className={`flex-shrink-0 border-[4px] border-primary-act rounded-full ${
                          shouldUseBulletLayout ? "mt-margin-xs" : ""
                        } ${item.disabled ? "opacity-light-opacity" : ""}`}
                      >
                        <SVGComponent
                          url={getRowImageUrl(item)}
                          color={item?.imgColor}
                          height={"var(--icon-lg)"}
                          width={"var(--icon-lg)"}
                          alt=""
                        />
                      </div>
                    )}

                    <div className="flex flex-col  flex-1 min-w-0">
                      <p
                        className={`text-font-sm font-semibold ${
                          item.disabled
                            ? "text-text-primary opacity-light-opacity"
                            : "text-text-primary"
                        }`}
                      >
                        {item.text}
                      </p>

                      {item.bulletPoint &&
                        Array.isArray(item.bulletPoint) &&
                        item.bulletPoint.length >
                          GlobalConfiguration.lengthCheckZero && (
                          <div
                            className={`flex flex-col gap-gap-xs transition-opacity duration-animation-slow ease-in-out ${
                              shouldShowBullets(item)
                                ? "opacity-100 pt-padding-sm"
                                : "opacity-0 h-0 overflow-hidden"
                            }`}
                          >
                            {item.bulletPoint.map((bullet, bulletIndex) => (
                              <div
                                key={bulletIndex}
                                className={`flex items-start  gap-gap-xs text-font-xs font-normal leading-snug ${
                                  item.disabled
                                    ? "text-text-secondary opacity-light-opacity"
                                    : "text-text-secondary"
                                }`}
                              >
                                <span className="flex-shrink-0 select-none leading-snug">
                                  •
                                </span>
                                <span className="min-w-0">
                                  {linkIdentifier(bullet)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>

                    <img
                      className={`w-icon-md h-icon-md flex-shrink-0 ${
                        shouldUseBulletLayout ? "mt-margin-xs" : ""
                      } ${item.disabled ? "opacity-light-opacity" : ""}`}
                      src={
                        isExpanded
                          ? ICONS.RADIO_BUTTON_SELECTED
                          : ICONS.RADIO_BUTTON_NOT_SELECTED
                      }
                      alt=""
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="w-full gap-gap-sm px-padding-md flex justify-end pt-padding-md flex-shrink-0">
            <PrimaryButton
              btnText={"Cancel"}
              btnType={btnOneBtnType}
              handleFunction={() => {
                if (!btnOneIsDisabled) {
                  setAnimate(true);
                  setrowSelected(false);
                  setTimeout(() => {
                    safeBtnOneHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              isDisabled={btnOneIsDisabled}
              enabledTextColor={btnOneEnabledTextColor}
              disabledTextColor={btnOneDisabledTextColor}
              enabledBgColor={btnOneEnabledBgColor}
              disabledBgColor={btnOneDisabledBgColor}
              outLineColor={btnOneOutLineColor}
              fullWidthRequired={btnOneFullWidthRequired}
              hoverOption={btnOneHoverOption}
              isLoading={btnOneIsLoading}
              buttonRef={btnOneButtonRef}
              inActiveVariantNeeded={btnOneInActiveVariantNeeded}
              isLoaderNeeded={btnOneIsLoaderNeeded}
            />
            <PrimaryButton
              btnText={btnTwoText}
              handleFunction={() => {
                if (!btnTwoIsDisabled) {
                  setAnimate(true);
                  setrowSelected(false);
                  setTimeout(() => {
                    safeBtnTwoHandler();
                  }, IntervalConfiguration.intervalSemiMid);
                }
              }}
              enabledBgColor={btnTwoEnabledBgColor}
              outLineColor={btnTwoOutLineColor}
              enabledTextColor={btnTwoEnabledTextColor}
              isDisabled={btnTwoIsDisabled}
              fullWidthRequired={btnTwoFullWidthRequired}
              hoverOption={btnTwoHoverOption}
              btnType={btnTwoBtnType}
              disabledTextColor={btnTwoDisabledTextColor}
              disabledBgColor={btnTwoDisabledBgColor}
              isLoading={btnTwoIsLoading}
              buttonRef={btnTwoButtonRef}
              inActiveVariantNeeded={btnTwoInActiveVariantNeeded}
              isLoaderNeeded={btnTwoIsLoaderNeeded}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCommonPopup;
